import axios from 'axios'
import endpointConfig from './endpointConfig'
import apiConfigs from 'app/configs/apiConfigs'
import * as R from 'ramda'

axios.interceptors.request.use(
  (config) => {
    if (
      config.url &&
      [apiConfigs.managerServiceURL, apiConfigs.apiPrefixURL].some((url) =>
        config.url.startsWith(url),
      )
    ) {
      config.withCredentials = true
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

const backend = {
  healtCheck: (headers) =>
    axios({
      headers,
      url: endpointConfig.healtCheck(),
      method: 'get',
    }),
  getBillingAddress: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.billingAddress(placeId),
      method: 'get',
    }),
  getDeals: ({ placeId, ...props }, headers) =>
    axios({
      headers,
      url: endpointConfig.deals(placeId),
      method: 'get',
      params: props,
    }),
  getBillingShippingAddress: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.billingShippingAddress(placeId),
      method: 'get',
    }),
  createBillingShippingAddress: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingShippingAddress(placeId),
      method: 'post',
      data,
    }),
  updateBillingShippingAddress: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateBillingShippingAddress({
        placeId,
        id: data.id,
      }),
      method: 'patch',
      data,
    }),

  getBillingHistory: ({ placeId, page, limit }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingHistory(placeId),
      method: 'get',
      params: { page, limit },
    }),

  getBillingCustomer: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.billingCustomer(placeId),
      method: 'get',
    }),

  getBillingToken: ({ placeId, isCustomerIncluded }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingToken(placeId),
      method: 'get',
      params: { include_customer: Number(isCustomerIncluded) },
    }),

  purchasePlan: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.purchasePlan({ placeId, planId: data.planId }),
      method: 'post',
      data,
    }),

  rechargePlan: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingRecharge(placeId),
      method: 'post',
      data,
    }),

  cancelPlan: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.cancelPlan(placeId),
      method: 'post',
    }),

  getPaymentMethod: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.billingPaymentMethod(placeId),
      method: 'get',
    }),

  setDefaultPaymentMethod: ({ placeId, token }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingPaymentMethod(placeId),
      method: 'patch',
      data: {
        token,
        default: true,
      },
    }),

  createPaymentMethod: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingPaymentMethod(placeId),
      method: 'post',
      data,
    }),

  deletePaymentMethod: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingPaymentMethod(placeId),
      method: 'delete',
      data,
    }),

  verifyPaymentMethod: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingVerifyPaymentMethod(placeId),
      method: 'post',
      data,
    }),

  updatePaymentMethod: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingPaymentMethod(placeId),
      method: 'post',
      data,
    }),

  getUsageHistory: ({ placeId, page, limit }, headers) =>
    axios({
      headers,
      url: endpointConfig.usageHistory(placeId),
      method: 'get',
      params: { page, limit },
    }),
  updateBillingAddress: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.billingAddress(placeId),
      method: 'patch',
      data,
    }),

  confirmChangingPlaceEmail: ({ token }) =>
    axios({
      url: endpointConfig.confirmChangingPlaceEmail(),
      method: 'PATCH',
      data: { token },
    }),

  activatePlace: (token) =>
    axios({
      url: endpointConfig.activate(token),
      method: 'get',
    }),

  checkEmail: ({ email, isExistence }) =>
    axios({
      url: endpointConfig.checkEmail(),
      method: 'post',
      data: R.reject(R.isNil, { email, isExistence }),
    }),

  register: (data) =>
    axios({
      url: endpointConfig.register(),
      method: 'post',
      data,
    }),

  postCuratedPlaceSingUp: (data) =>
    axios({
      url: endpointConfig.curatedPlaceSingUp(),
      method: 'post',
      data,
    }),

  login: ({ username, password, isKeepMeSignedIn }, headers) =>
    axios({
      headers,
      url: endpointConfig.auth(),
      method: 'post',
      data: {
        email: username,
        password: password,
        clientId: apiConfigs.clientId,
        clientSecret: apiConfigs.clientSecret,
        rememberMe: isKeepMeSignedIn,
      },
    }),

  logout: (headers) =>
    axios({
      headers,
      url: endpointConfig.signOut(),
      method: 'post',
    }),

  resetPassword: ({ code, password }) =>
    axios({
      url: endpointConfig.resetPassword(),
      method: 'put',
      data: { token: code, password },
    }),

  forgot: ({ userName, requestId }) => {
    return axios({
      url: endpointConfig.forgot(),
      method: 'post',
      data: { email: userName, requestId },
    })
  },

  resentPlaceVerificationEmail: (headers, placeId) => {
    return axios({
      headers,
      url: endpointConfig.resentPlaceVerificationEmail(placeId),
      method: 'post',
    })
  },

  cancelPlaceVerificationEmail: (headers, placeId) => {
    return axios({
      headers,
      url: endpointConfig.cancelPlaceVerificationEmail(placeId),
      method: 'post',
    })
  },

  refreshToken: ({ token }, headers) =>
    axios({
      headers,
      url: endpointConfig.refreshToken(),
      method: 'post',
      data: { token },
    }),
  refreshTokenManager: (_, headers) =>
    axios({
      headers,
      url: endpointConfig.authV2.refreshTokenManager(),
      method: 'post',
    }),

  loginWithSSO: ({ token }, headers) =>
    axios({
      headers: { ...headers },
      url: endpointConfig.loginWithSSO(),
      method: 'post',
      data: { hash: token },
    }),

  generateIntegrationAPIToken: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.generateAPIToken(placeId),
      method: 'post',
    }),

  me: (_, headers) =>
    axios({
      headers,
      url: endpointConfig.my(),
      method: 'get',
    }),

  getPlaceById: (id, headers) =>
    axios({
      headers,
      url: endpointConfig.places(id),
      method: 'get',
    }),

  getPermissions: (headers) =>
    axios({
      headers,
      url: endpointConfig.getPermissions(),
      method: 'get',
    }),

  getRoles: ({ placeId, page, limit, sort }, headers) =>
    axios({
      headers,
      url: endpointConfig.roles(placeId),
      method: 'get',
      params: { page, limit, sort },
    }),

  deleteRoles: ({ placeId, ids }, headers) =>
    axios({
      headers,
      url: endpointConfig.roles(placeId),
      method: 'delete',
      data: { ids },
    }),

  createRole: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.roles(placeId),
      method: 'post',
      data,
    }),

  updateRole: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateRole({ placeId, roleId: data.id }),
      method: 'put',
      data,
    }),

  categories: ({ page, limit }) =>
    axios({
      url: endpointConfig.categories(),
      method: 'get',
      params: { page, limit },
    }),

  /**
   *
   * type Data = {
   *  name :: String,
   *  address :: String,
   *  contactUrl :: String,
   *  counrtyCode :: String,
   *  phoneNumber :: String,
   *  email :: String
   * }
   */
  saveProfileInfo: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.places(placeId),
      method: 'patch',
      data,
    }),

  savePlacePublic: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.placePublic(placeId),
      method: 'put',
      data,
    }),

  savePlaceProfileEntity: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.places(placeId),
      method: 'put',
      data,
    }),

  uploadProfilePhoto: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.placeLogo(placeId),
      method: 'patch',
      data,
    }),
  uploadLineProfile: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.uploadLineProfile(placeId),
      method: 'post',
      data,
    }),

  groups: ({ placeId, page, ...props }, headers) => {
    return axios({
      headers,
      url: endpointConfig.groups(placeId),
      method: 'get',
      params: { page, ...props },
    })
  },

  groupsWithTags: ({ placeId, ...props }, headers) => {
    return axios({
      headers,
      url: endpointConfig.groupsWithTags(placeId),
      method: 'get',
      params: { ...props },
    })
  },

  getGroupWithSubscriber: (
    { placeId, type, status, channels, scope },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getGroupWithSubscriber(placeId),
      method: 'get',
      params: { type, status, channels, scope },
    }),

  searchRecipient: ({ placeId, keyword, scope }, headers) =>
    axios({
      headers,
      url: endpointConfig.searchRecipient(placeId),
      method: 'get',
      params: { keyword, scope },
    }),

  groupDetails: ({ groupId, perPage }, headers) =>
    axios({
      headers,
      url: endpointConfig.groupDetails(groupId),
      method: 'get',
      params: { groupId, perPage },
    }),
  groupsV4: ({ placeId, page, ...props }, headers) => {
    return axios({
      headers,
      url: endpointConfig.groupsV4(placeId),
      method: 'get',
      params: { page, ...props },
    })
  },
  groupDetailV4: ({ placeId, groupId, perPage }, headers) =>
    axios({
      headers,
      url: endpointConfig.groupDetailV4({ groupId, placeId }),
      method: 'get',
      params: { groupId, perPage },
    }),

  addSubgroup: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.addSubgroup(requestData.groupId),
      method: 'post',
      data: { ...requestData },
    }),

  attachSubscribers: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.attachSubscribers(requestData.tagId),
      method: 'put',
      data: { ...requestData },
    }),
  addSubscriberToTags: ({ placeId, tagId, subscriberIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.addSubscriberToTags({ placeId, tagId }),
      method: 'put',
      data: { subscriberIds },
    }),

  detachSubscribersSubgroups: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.detachSubscribersSubgroups(requestData.tagId),
      method: 'put',
      data: { ...requestData },
    }),

  detachSubscribersFromGroup: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.detachSubscribersFromGroup(requestData.groupId),
      method: 'put',
      data: { ...requestData },
    }),

  createSubscriber: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.createSubscriber(requestData.placeId),
      method: 'post',
      data: { ...requestData.data },
    }),

  updateGroup: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.updateGroup(requestData.groupId),
      method: 'put',
      data: { ...requestData },
    }),

  updateSubGroup: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.updateSubGroup(requestData.tagId),
      method: 'put',
      data: { ...requestData },
    }),

  deleteSubgroup: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.updateSubGroup(requestData.tagId),
      method: 'delete',
    }),

  createGroup: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.createGroup(requestData.placeId),
      method: 'post',
      data: { ...requestData },
    }),

  bulkAssignSubscribersToGroups: ({ placeId, ...requestData }, headers) =>
    axios({
      headers,
      url: endpointConfig.bulkAssignSubscribersToGroups(placeId),
      method: 'post',
      data: { ...requestData },
    }),

  bulkDeleteSubscribers: ({ placeId, ...requestData }, headers) =>
    axios({
      headers,
      url: endpointConfig.bulkDeleteSubscribers(placeId),
      method: 'delete',
      data: { ...requestData },
    }),

  deleteGroups: ({ groupIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteGroups(),
      method: 'delete',
      data: { ids: groupIds },
    }),

  subscribers: (
    {
      placeId,
      page,
      limit,
      sort,
      keyword,
      status,
      tag,
      subgroups,
      groups,
      badContact,
      membershipCardIds,
      notificationMethods,
    },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.subscribers(placeId),
      method: 'get',
      params: {
        placeId,
        page,
        limit,
        sort,
        keyword,
        status,
        tag,
        subgroups,
        groups,
        badContact,
        membershipCardIds,
        notificationMethods,
      },
    }),

  subscribersV2: (
    {
      placeId,
      page,
      limit,
      sort,
      keyword,
      status,
      tag,
      subgroups,
      groups,
      badContact,
      membershipCardIds,
      notificationMethods,
      source,
      zipCode,
    },
    headers,
    { signal } = {},
  ) =>
    axios({
      headers,
      url: endpointConfig.subscribersV2.get(placeId),
      method: 'get',
      signal,
      params: {
        placeId,
        page,
        limit,
        sort,
        keyword,
        status,
        tag,
        subgroups,
        groups,
        badContact,
        membershipCardIds,
        notificationMethods,
        source,
        zipCode,
      },
    }),

  subscriberNotInTags: (
    { placeId, tagId, page, limit, sort, keyword },
    headers,
    { signal } = {},
  ) =>
    axios({
      headers,
      url: endpointConfig.subscriberNotInTags.get({ tagId, placeId }),
      method: 'get',
      signal,
      params: {
        placeId,
        page,
        limit,
        sort,
        keyword,
      },
    }),

  groupSubscriberV2: (
    { placeId, page, limit, sort, keyword, groupId, tagIds },
    headers,
    { signal } = {},
  ) =>
    axios({
      headers,
      url: endpointConfig.groupsV2.getSubscribers({ placeId, groupId }),
      method: 'get',
      signal,
      params: {
        page,
        limit,
        sort,
        keyword,
        tagIds,
      },
    }),

  subscribersCountForStatuses: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribersCountForStatuses(placeId),
      method: 'get',
      params: {
        placeId,
      },
    }),
  subscribersTotal: (
    { placeId, subgroups, subscriber, status, notificationMethods },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.subscribers(placeId),
      method: 'get',
      params: { placeId, subgroups, subscriber, status, notificationMethods },
    }),

  recentSubscribers: ({ placeId, page, limit, status }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribers(placeId),
      method: 'get',
      params: { placeId, page, limit, status },
    }),

  teamMembers: ({ placeId, page, limit, sort, keyword }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMembers(placeId),
      method: 'get',
      params: { placeId, page, limit, sort, keyword },
    }),

  teamMemberById: ({ placeId, teamMemberId }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMember({ placeId, teamMemberId }),
      method: 'get',
    }),

  createTeamMember: ({ placeId, ...requestData }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMembers(placeId),
      method: 'post',
      data: { ...requestData },
    }),

  updateTeamMember: ({ placeId, ...requestData }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateTeamMember({ placeId, userId: requestData.id }),
      method: 'put',
      data: { ...requestData },
    }),

  searchPlace: ({ keywords }) =>
    axios({
      url: endpointConfig.search(),
      method: 'get',
      params: { keywords },
    }),

  getNotifications: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotifications(placeId),
      method: 'get',
      params: data,
    }),

  getSubscriberById: ({ subscriberId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSubscriberById(subscriberId),
      method: 'get',
    }),

  placeLanguageCompose: (
    {
      placeId,
      subgroups,
      subscribers,
      status,
      topicIds,
      channels,
      composeTitle,
      composeBody,
      composeDescription,
      voucherTitle,
      voucherDescription,
      composeQuestion,
      composeOptions,
      isAllPlaceLanguage = true,
    },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.placeLanguageCompose(placeId),
      method: 'post',
      data: {
        subgroups,
        subscribers,
        status,
        topicIds,
        channels,
        composeTitle,
        composeBody,
        composeDescription,
        voucherTitle,
        voucherDescription,
        composeQuestion,
        composeOptions,
        isAllPlaceLanguage,
      },
    }),

  placeLanguage: (
    {
      placeId,
      subgroups,
      subscribers,
      status,
      topicIds,
      channels,
      isAllPlaceLanguage = true,
    },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.placeLanguage(placeId),
      method: 'get',
      params: {
        subgroups,
        subscribers,
        status,
        topicIds,
        channels,
        isAllPlaceLanguage,
      },
    }),

  subscriberNumber: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscriberNumber(placeId),
      method: 'get',
      params: {},
    }),

  getPlaceCompletedSteps: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getPlaceCompletedSteps(placeId),
      method: 'get',
    }),

  sendNotification: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.notifications(placeId),
      method: 'post',
      data,
    }),

  updateNotification: ({ notificationId, notification }, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationById(notificationId),
      method: 'put',
      data: notification,
    }),

  getNotification: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotificationById({ placeId, notificationId }),
      method: 'get',
    }),

  unsendNotification: (notificationId, headers) =>
    axios({
      headers,
      url: endpointConfig.unsendNotification(notificationId),
      method: 'patch',
      data: { status: true },
    }),

  cancelEventNotification: ({ notificationId, reason }, headers) =>
    axios({
      headers,
      url: endpointConfig.cancelEventNotification(notificationId),
      method: 'patch',
      data: { reason },
    }),

  getGeoInfo: (headers) =>
    axios({
      headers,
      url: endpointConfig.ipapiUrl(),
      method: 'get',
    }),

  getPlans: (headers) =>
    axios({
      url: endpointConfig.plans(),
      method: 'get',
      params: {},
    }),

  translate: (data, headers) =>
    axios({
      headers,
      url: endpointConfig.translate(),
      method: 'post',
      ...data,
    }),

  interestBeacon: ({ placeId, ids }, headers) =>
    axios({
      headers,
      url: endpointConfig.interestBeacon(placeId),
      method: 'post',
      data: { entityType: 1 },
    }),

  deleteTeamMembers: ({ placeId, ids }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMembers(placeId),
      method: 'delete',
      data: { ids },
    }),

  checkValidYoutubeById: (videoId) =>
    axios({
      url: endpointConfig.googleYoutube(videoId),
      method: 'get',
      params: {},
    }),

  getRandomPlaces: () =>
    axios({
      url: endpointConfig.getRandomPlaces(),
      method: 'get',
      params: {},
    }),

  updateAdminPassword: ({ currentPassword, newPassword }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateAdminPassword(),
      method: 'put',
      data: { currentPassword, newPassword },
    }),

  getInitialEmail: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialEmail(placeId),
      method: 'get',
      params: {},
    }),

  getInitialSms: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialSms(placeId),
      method: 'get',
      params: {},
    }),

  updateInitialEmail: ({ placeId, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialEmail(placeId),
      method: 'put',
      data,
    }),

  updateInitialSms: ({ placeId, data: { body } }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialSms(placeId),
      method: 'put',
      data: { body },
    }),

  publishInitialEmail: ({ placeId, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialEmailPublish(placeId),
      method: 'post',
      data,
    }),

  publishInitialSms: ({ placeId, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialSmsPublish(placeId),
      method: 'post',
      data,
    }),

  updateAdminProfile: (data, headers) =>
    axios({
      headers,
      url: endpointConfig.my(),
      method: 'put',
      data: data,
    }),

  notificationLanguage: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationLanguage(placeId, notificationId),
      method: 'get',
      params: { placeId, notificationId },
    }),

  getInitialNotification: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialNotification(placeId),
      method: 'get',
      params: { placeId },
    }),

  updateInitialNotification: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.initialNotification(placeId),
      method: 'put',
      data: { ...data },
    }),
  // createComment: (commentItems, headers) =>
  //   axios({
  //     headers,
  //     url: endpointConfig.createComment(),
  //     method: 'post',
  //     data: commentItems,
  //   }),
  // deleteComment: (id, headers) =>
  //   axios({
  //     headers,
  //     url: endpointConfig.modifyComment(id),
  //     method: 'delete',
  //   }),
  // updateComment: ({ id, text }, headers) =>
  //   axios({
  //     headers,
  //     url: endpointConfig.modifyComment(id),
  //     method: 'patch',
  //     data: { text },
  //   }),
  approveSubscriber: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.approveSubscriber(requestData.placeId),
      method: 'patch',
      data: requestData,
    }),
  updateSubscriber: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.updateSubscriber(requestData.id),
      method: 'put',
      data: requestData,
    }),
  deleteSubscribersCRMCustomField: ({ subscriberId, fieldId }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteSubscribersCRMCustomField(subscriberId),
      method: 'delete',
      data: { id: fieldId },
    }),
  getLanguage: ({ page, limit, sort }, headers) =>
    axios({
      headers,
      url: endpointConfig.languages(),
      method: 'get',
      params: { page, limit, sort },
    }),
  completeAccountSetupStep: ({ placeId, stepId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getPlaceCompletedSteps(placeId),
      method: 'patch',
      params: { stepId },
    }),

  deleteSubscribers: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteSubscribers(),
      method: 'delete',
      data: requestData,
    }),

  checkImportProcess: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.checkImportProcess(),
      method: 'post',
      data: requestData,
    }),

  checkCSVValidationToLambda: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.checkCSVValidationToLambda(requestData.placeId),
      method: 'post',
      data: requestData,
    }),

  importCSVToLambda: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.importCSVToLambda(requestData.placeId),
      method: 'post',
      data: requestData,
    }),

  cancelReminder: (notiId, headers) =>
    axios({
      headers,
      url: endpointConfig.cancelReminder(notiId),
      method: 'delete',
    }),
  cancelSchduleNotification: (notiId, headers) =>
    axios({
      headers,
      url: endpointConfig.cancelScheduleNotification(notiId),
      method: 'patch',
    }),
  getAWSCognitoAuth: (headers) =>
    axios({
      headers,
      url: endpointConfig.getAWSCognitoAuth(),
      method: 'get',
    }),
  settings: (headers) =>
    axios({
      headers,
      url: endpointConfig.settings(),
      method: 'get',
    }),
  getNotiEventSubscribers: ({ notificationId, action, page, limit }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotiEventSubscribers({ notificationId, action }),
      method: 'get',
      params: { page, limit },
    }),
  downloadEventNotification: ({ notificationId, action }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotiEventSubscribers({ notificationId, action }),
      method: 'get',
      responseType: 'blob',
    }),
  googleLogin: (headers) =>
    axios({
      headers,
      url: endpointConfig.googleLogin(),
      method: 'get',
    }),
  getGoogleContacts: ({ placeId, code }, headers) =>
    axios({
      headers,
      url: endpointConfig.getGoogleContacts({ placeId, code }),
      method: 'get',
    }),
  importGoogleContacts: ({ placeId, contacts, tagIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.importGoogleContacts({ placeId }),
      method: 'post',
      data: { contacts, tagIds },
    }),

  ttsSettings: (headers) =>
    axios({
      headers,
      url: endpointConfig.ttsSettings(),
      method: 'get',
    }),

  checkAudioExists: ({ notificationId, langId }, headers) =>
    axios({
      headers,
      url: endpointConfig.checkAudioExists(),
      method: 'post',
      data: { notificationId, langId },
    }),

  getConversationList: (
    { placeId, limit, last_key, search, ...otherParam },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getPlaceConversation(placeId),
      method: 'get',
      params: { placeId, limit, last_key, search, ...otherParam },
    }),

  getConversationDetail: (historyId, headers) =>
    axios({
      headers,
      url: endpointConfig.getConversationDetail(historyId),
      method: 'get',
    }),

  authConversation: (requestData) =>
    axios({
      url: endpointConfig.authConversation(),
      method: 'post',
      data: requestData,
    }),

  getConversationHistory: (token, history_id, limit, lastKey) =>
    axios({
      headers: {
        Authorization: `Bearer ${token}`,
      },
      url: endpointConfig.getConversationHistory(history_id),
      method: 'get',
      params: {
        history_id: history_id,
        limit: limit,
        last_key: lastKey,
      },
    }),
  createUserTransfer: ({ placeId, inviteeUserId, password }, headers) =>
    axios({
      headers,
      url: endpointConfig.placeUserTransferRequest({ placeId }),
      method: 'post',
      data: {
        inviteeUserId,
        password,
      },
    }),
  changeTransferStatus: ({ transferRequestId, status }, headers) =>
    axios({
      headers,
      url: endpointConfig.userTransferRequest({ transferRequestId }),
      method: 'patch',
      data: {
        status,
      },
    }),
  setTransferIsSeen: ({ transferRequestId }, headers) =>
    axios({
      headers,
      url: endpointConfig.userTransferIsSeen({ transferRequestId }),
      method: 'patch',
    }),
  getBeacons: ({ placeId, page, limit, sort, keyword, status }, headers) =>
    axios({
      headers,
      url: endpointConfig.getBeacons(placeId),
      method: 'get',
      params: { page, limit, sort, keyword, status },
    }),
  createBeacons: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.getBeacons(placeId),
      method: 'post',
      data,
    }),
  deleteBeacons: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.getBeacons(placeId),
      method: 'delete',
      data,
    }),
  updateBeacons: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateBeacon({ placeId, beaconId: data.id }),
      method: 'patch',
      data,
    }),
  updateBeaconMetrics: ({ placeId, beaconId }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateBeaconMetrics({ placeId, beaconId }),
      method: 'patch',
    }),
  checkBeaconPhysicalAddress: (physicalAddress, headers) =>
    axios({
      headers,
      url: endpointConfig.checkBeaconPhysicalAddress(physicalAddress),
      method: 'get',
    }),
  getNotificationBeacons: ({ placeId, page, limit, sort, keyword }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotificationBeacons({ placeId }),
      method: 'get',
      params: { page, limit, sort, keyword },
    }),
  deleteNotificationBeacons: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteNotificationBeacons(placeId),
      method: 'patch',
      data: { status: true, notificationIds: notificationId },
    }),
  createNotificationBeacon: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.createNotificationBeacon(placeId),
      method: 'post',
      data,
    }),
  updateNotificationBeacon: ({ placeId, notificationId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationBeaconById({ placeId, notificationId }),
      method: 'put',
      data,
    }),
  updateStatusNotificationBeacon: (
    { placeId, notificationId, isActive },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.updateStatusNotificationBeacon({
        placeId,
        notificationId,
      }),
      method: 'put',
      data: { isActive, notificationId },
    }),
  updateCareCheck: ({ placeId, shouldSeeCareCheck }, headers) =>
    axios({
      headers,
      url: endpointConfig.careCheck({ placeId }),
      method: 'patch',
      data: { shouldSeeCareCheck },
    }),
  getSubscriber: ({ subscriberId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSubscriber(subscriberId),
      method: 'get',
    }),
  getVoucherInfo: ({ placeId, voucherId, parentId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getVoucherInfo({
        placeId,
        voucherId,
        parentId,
      }),
      method: 'get',
    }),
  getCuratedCheck: (googlePlaceId, headers) =>
    axios({
      headers,
      url: endpointConfig.curatedCheck(googlePlaceId),
      method: 'get',
    }),
  approveRedemption: ({ placeId, parentId, alertId, voucherId }, headers) =>
    axios({
      headers,
      url: endpointConfig.approveRedemption({ placeId }),
      method: 'post',
      data: { parentId, alertId, voucherId },
    }),
  rejectRedemption: ({ placeId, parentId, alertId, voucherId }, headers) =>
    axios({
      headers,
      url: endpointConfig.rejectRedemption({ placeId }),
      method: 'post',
      data: { parentId, alertId, voucherId },
    }),
  groupAndIndividualSubscribersCount: (
    { placeId, subgroupIds, subscriberIds, status },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.groupAndIndividualSubscribersCount(placeId),
      method: 'get',
      params: { placeId, subgroupIds, subscriberIds, status },
    }),
  getGroupCategories: (headers) =>
    axios({
      headers,
      url: endpointConfig.getGroupCategories(),
      method: 'get',
    }),
  updatePlaceCategory: ({ placeId, categoryId }, headers) =>
    axios({
      headers,
      url: endpointConfig.updatePlaceCategory(placeId),
      method: 'patch',
      data: { categoryId },
    }),
  findJob: ({ data }, headers) =>
    axios({
      headers,
      url: endpointConfig.findJob(),
      method: 'post',
      data,
    }),
  getCountries: (headers) =>
    axios({
      headers,
      url: endpointConfig.countries(),
      method: 'get',
    }),
  getNationalities: (headers) =>
    axios({
      headers,
      url: endpointConfig.nationalities(),
      method: 'get',
    }),
  getStatesByCountryId: ({ countryId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getStatesByCountryId(countryId),
      method: 'get',
    }),
  createNotes: (data, headers) =>
    axios({
      headers,
      url: endpointConfig.createNotes(),
      method: 'post',
      data,
    }),
  updateNotes: ({ text, noteId }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateNotes(noteId),
      method: 'put',
      data: { text },
    }),
  getNotesBySubscriberId: (params, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotesBySubscriberId(),
      method: 'get',
      params,
    }),
  deleteNoteById: ({ noteId }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteNoteById(noteId),
      method: 'delete',
    }),
  uploadImage: ({ placeId, data, onProgressBar, index }, headers) =>
    axios({
      headers,
      url: endpointConfig.uploadImage(placeId),
      method: 'post',
      data,
      onUploadProgress: (event) => {
        onProgressBar(event, index)
      },
    }),
  downloadImageFromUrl: ({ url }) =>
    axios({
      url,
      method: 'get',
      responseType: 'blob',
    }),
  messageDismiss: ({ id }, headers) =>
    axios({
      headers,
      url: endpointConfig.messageDismiss(id),
      method: 'put',
    }),
  allMessagesDismiss: (data, headers) =>
    axios({
      headers,
      url: endpointConfig.allMessagesDismiss(data.placeId),
      method: 'put',
      data,
    }),
  getMemberCard: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.getMemberCard(placeId),
      method: 'get',
      params: data,
    }),
  detectLanguage: (data, headers) =>
    axios({
      headers,
      url: endpointConfig.detectLanguage(),
      method: 'POST',
      data,
    }),
  validGroupForMemberCard: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.validGroupForMemberCard(placeId),
      method: 'get',
    }),
  updateMembershipCard: ({ placeId, id, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateMembershipCard({ placeId, id }),
      method: 'patch',
      data,
    }),
  createMembershipCard: ({ placeId, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.createMembershipCard(placeId),
      method: 'post',
      data,
    }),
  getMemberCardOptions: ({ placeId, subscriberId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getMemberCardOptions({ placeId, subscriberId }),
      method: 'get',
    }),
  getSubscriberMemberCard: ({ placeId, subscriberId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSubscriberMemberCard({ placeId, subscriberId }),
      method: 'get',
    }),
  createSubscriberMemberCard: ({ placeId, subscriberId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscriberMemberCard({ placeId, subscriberId }),
      method: 'post',
      data,
    }),
  updateSubscriberMemberCard: ({ placeId, subscriberId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscriberMemberCardById({ placeId, subscriberId }),
      method: 'patch',
      data,
    }),
  deleteMembershipCard: ({ placeId, cardId }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteMembershipCard({ placeId, cardId }), ///v1/places/{placeId}/membership-cards/{id}
      method: 'delete',
    }),
  getNotificationTimelineMetrics: ({ timeLineId, metricsType }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotificationTimelineMetrics({
        timeLineId,
        metricsType,
      }),
      method: 'get',
    }),
  getNotificationMetrics: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotificationMetrics({ placeId, notificationId }),
      method: 'get',
    }),
  getNotificationLanguage: ({ notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getNotificationLanguage({ notificationId }),
      method: 'get',
    }),
  getSubscribersExportData: (params, placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.getSubscribersExportData(placeId),
      method: 'get',
      params,
    }),
  getDealsPromotionList: ({ placeId, ...props }, headers) =>
    axios({
      headers,
      url: endpointConfig.getDealsPromotionList({ placeId }),
      method: 'get',
      params: props,
    }),
  createVoucher: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.createVoucher(placeId),
      method: 'post',
      data,
    }),

  updateVoucher: ({ placeId, data, voucherId }, headers) => {
    return axios({
      headers,
      url: endpointConfig.updateVoucher({ placeId, voucherId }),
      method: 'put',
      data,
    })
  },
  conversationsArchive: ({ placeId, historyId }, headers) => {
    return axios({
      headers,
      url: endpointConfig.conversationsArchive({ placeId, historyId }),
      method: 'post',
    })
  },
  conversationsDelete: ({ placeId, historyId }, headers) => {
    return axios({
      headers,
      url: endpointConfig.conversationsDelete({ placeId, historyId }),
      method: 'post',
    })
  },
  getVoucherTemplateList: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getVoucherTemplateList({ placeId }),
      method: 'get',
    }),
  createDraftNotification: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.createDraftNotification(placeId),
      method: 'post',
      data,
    }),
  sendDraftNotification: ({ id, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.sendDraftNotification(id),
      method: 'put',
      data,
    }),
  getDraftNotifications: (
    { placeId, page, limit, sort, keyword, type },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getDraftNotifications(placeId),
      method: 'get',
      params: { placeId, page, limit, sort, keyword, type },
    }),
  getDraftNotificationsCount: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getDraftNotificationsCount(placeId),
      method: 'get',
    }),
  updateDraftNotification: ({ notificationId, notification }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateDraftNotification(notificationId),
      method: 'put',
      data: notification,
    }),
  deleteDraftNotification: ({ placeId, id }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteDraftNotification({ placeId, id }),
      method: 'delete',
    }),
  getPlacePhotos: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getPlacePhotos({ placeId }),
    }),
  deletePhotos: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.deletePhotos({ placeId }),
      method: 'post',
      data,
    }),
  uploadPhotos: ({ placeId, data, onProgressBar, index }, headers) =>
    axios({
      headers,
      url: endpointConfig.uploadPhotos({ placeId }),
      method: 'post',
      data: { photo: data },
      onUploadProgress: (event) => {
        onProgressBar(event, index)
      },
    }),
  openingHours: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.openingHours({ placeId }),
      method: 'get',
    }),
  saveOpeningHours: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.openingHours({ placeId }),
      method: 'post',
      data: { openingHours: data },
    }),
  getGenerateLandingUrl: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.generateLandingUrl({ placeId }),
      method: 'get',
    }),
  putUniqueName: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.uniqueName({ placeId }),
      method: 'patch',
      data,
    }),
  getEnvData: (headers) =>
    axios({
      headers,
      url: endpointConfig.envData(),
      method: 'get',
    }),
  cancelDealsPromotions: ({ placeId, voucherId }, headers) =>
    axios({
      headers,
      url: endpointConfig.cancelDealsPromotions({ placeId, voucherId }),
      method: 'post',
    }),
  translateSubscriberFullName: ({ placeId, subscriberId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.translateSubscriberFullName({
        placeId,
        subscriberId,
      }),
      method: 'patch',
      data,
    }),
  setTranslatedName: ({ placeId, subscriberId }, headers) =>
    axios({
      headers,
      url: endpointConfig.setTranslatedName({ placeId, subscriberId }),
      method: 'patch',
    }),
  getVoucherView: ({ html, voucherTemplateId, isStyle, placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getVoucherView({ placeId }),
      method: 'patch',
      data: { html, voucherTemplateId, isStyle },
    }),
  getSocialIntegrationSettings: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSocialIntegrationSettings({ placeId }),
      method: 'get',
    }),
  getSocialProviderPages: ({ placeId, socialProviderKey }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSocialProviderPages({
        placeId,
        socialProviderKey,
      }),
      method: 'get',
    }),
  updateSocialPageConnectionStatus: (
    { placeId, socialProviderKey, pageId, status },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.updateSocialPageConnectionStatus({
        placeId,
        socialProviderKey,
        pageId,
      }),
      data: {
        status,
      },
      method: 'patch',
    }),
  connectSocialProvider: ({ socialProviderKey, code, placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.connectSocialProvider({ placeId }),
      method: 'post',
      data: { code: code, type: socialProviderKey },
    }),
  connectSocialProviderTwitterConnect: (
    { oauthToken, oauthVerifier, type, placeId },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.connectSocialProviderTwitter({ placeId }),
      method: 'post',
      data: {
        oauthToken: oauthToken,
        oauthVerifier: oauthVerifier,
        type: type,
      },
    }),
  disconnectSocialProvider: ({ socialProviderKey, placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.disconnectSocialProvider({ placeId }),
      method: 'patch',
      data: socialProviderKey ? { type: socialProviderKey } : {},
    }),
  updateShowLanding: ({ showLanding, placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateShowLanding({ placeId, showLanding }),
      method: 'patch',
      data: { showLanding },
    }),
  getSubscriberCount: (
    { placeId, subgroupIds, subscriberIds, status, topicIds, scope },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.subscriberCount(placeId),
      method: 'get',
      params: { placeId, subgroupIds, subscriberIds, status, topicIds, scope },
    }),
  getSubscriberCountForReceive: (
    { placeId, subgroupIds, subscriberIds, status, topicIds, channels },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getSubscriberCountForReceive(placeId),
      method: 'get',
      params: {
        placeId,
        subgroupIds,
        subscriberIds,
        status,
        topicIds,
        channels,
      },
    }),

  createLanding: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.createLanding({ placeId }),
      method: 'post',
    }),
  getRecipientCountForTimeline: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.recipientCountForTimeline({
        placeId,
        notificationId,
      }),
      method: 'get',
    }),
  getPlaceNotificationsStatistics: ({ placeId }, headers) => {
    axios({
      headers,
      url: endpointConfig.placeNotificationsStatistics({
        placeId,
      }),
      method: 'get',
    })
  },
  getPlacePlanInfo: ({ placeId, cancelToken }, headers) =>
    axios({
      headers,
      url: endpointConfig.placePlanInfo(placeId),
      method: 'get',
      cancelToken: cancelToken.token,
    }),
  getPlaceLastNotification: ({ placeId, cancelToken }, headers) =>
    axios({
      headers,
      url: endpointConfig.placeLastNotification(placeId),
      method: 'get',
      cancelToken: cancelToken.token,
    }),
  getPlaceSubscribersGrowth: ({ placeId, period, cancelToken }, headers) =>
    axios({
      headers,
      url: endpointConfig.placeSubscribersGrowth(placeId),
      method: 'get',
      params: { period },
      cancelToken: cancelToken.token,
    }),
  getTopicList: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.getTopicList(placeId),
      method: 'get',
      params: data,
    }),
  createTopic: ({ placeId, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.createTopic(placeId),
      method: 'post',
      data,
    }),
  updateTopic: ({ placeId, ...data }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateTopic({ placeId, topicId: data.id }),
      method: 'put',
      data,
    }),
  deleteTopic: ({ placeId, id }, headers) =>
    axios({
      headers,
      url: endpointConfig.deleteTopic({ placeId, id }),
      method: 'delete',
    }),
  getSelectedTopics: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.selectedTopics({ placeId, notificationId }),
      method: 'get',
    }),
  getNotificationSocialMediaInteractions: (
    { placeId, notificationId, cancelToken },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.notificationSocialMediaInteractions({
        placeId,
        notificationId,
      }),
      method: 'get',
      cancelToken: cancelToken.token,
    }),
  hideCreditModal: ({ placeId, status }, headers) => {
    axios({
      headers,
      url: endpointConfig.hideCreditModal({ placeId, status }),
      method: 'patch',
      data: { status },
    })
  },
  getUsageHistoryCount: ({ placeId, date, entityType }, headers) =>
    axios({
      headers,
      url: endpointConfig.getUsageHistoryCount(placeId),
      method: 'get',
      params: { date, type: entityType },
    }),
  getAdvancedEditorUploadInfo: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.advancedEditorUploadInfo({ placeId }),
      method: 'post',
    }),
  getPublicNotificationLink: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.publicNotificationLink({ placeId, notificationId }),
      method: 'get',
    }),
  getTranslationLanguageById: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.translationLanguage({ placeId, notificationId }),
      method: 'get',
    }),

  markAsCorrectSubscriber: (subscriberId, headers) =>
    axios({
      headers,
      url: endpointConfig.markAsCorrectSubscriber(subscriberId),
      method: 'patch',
    }),
  phoneValidation: ({ placeId, phoneNumber }, headers) =>
    axios({
      headers,
      url: endpointConfig.phoneValidation(placeId),
      method: 'patch',
      data: { phoneNumber: phoneNumber },
    }),
  getSubscribersFollowTopics: (
    { placeId, topicsId, page, limit, sort },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getSubscribersFollowTopics({ placeId, topicsId }),
      method: 'get',
      params: { page, limit, sort },
    }),
  unfollowTopic: ({ placeId, topicsId, subscriberIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.unfollowTopic({ placeId, topicsId }),
      method: 'delete',
      data: { subscriberIds },
    }),
  updateDisplayHistory: (
    { placeId, notificationId, displayHistory },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.updateDisplayHistory({ placeId, notificationId }),
      method: 'put',
      data: { displayHistory },
    }),
  getSubscribersTopics: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSubscribersTopics({ placeId }),
      method: 'get',
    }),
  subscribersFollowTopics: ({ placeId, topicsId, subscriberIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribersFollowTopics({ placeId, topicsId }),
      method: 'put',
      data: { subscriberIds },
    }),
  getTopicId: ({ placeId, topicId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getTopicId({ placeId }),
      method: 'get',
      params: { topicId, notificationCount: 1 },
    }),
  cancelExpiration: (notiId, headers) =>
    axios({
      headers,
      url: endpointConfig.cancelExpiration(notiId),
      method: 'patch',
    }),
  uploadImageFile: ({ placeId, data, onProgressBar }, headers) =>
    axios({
      headers,
      url: endpointConfig.uploadImage(placeId),
      method: 'post',
      data,
      onUploadProgress: (event) => {
        onProgressBar(event)
      },
    }),
  uploadImageFileForCrm: ({ placeId, subscriberId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.uploadImageFileForCrm({ placeId, subscriberId }),
      method: 'post',
      data,
    }),
  autoApproval: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.autoApproval(placeId),
      method: 'patch',
      data,
    }),
  getAutomatedCallSetting: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getAutomatedCallSetting(placeId),
      method: 'get',
    }),
  updateAutomatedCallSetting: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateAutomatedCallSetting(placeId),
      method: 'put',
      data,
    }),
  getMessageRatio: (
    { placeId, subgroupIds, subscriberIds, status, topicIds, channels },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getMessageRatio(placeId),
      method: 'get',
      params: {
        placeId,
        subgroupIds,
        subscriberIds,
        status,
        topicIds,
        channels,
      },
    }),
  getBillingPlan: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getBillingPlan(placeId),
      method: 'get',
    }),
  getBillingAccumulateUsage: (
    { placeId, startDate, endDate, showAll, type, timezone },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getBillingAccumulateUsage(placeId),
      method: 'get',
      params: { startDate, endDate, showAll, type, timezone },
    }),
  getBillingPayment: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getBillingPayment(placeId),
      method: 'get',
    }),
  getUsageLineChart: (
    { placeId, startDate, endDate, type, timezone },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getUsageLineChart(placeId),
      method: 'get',
      params: { startDate, endDate, type, timezone },
    }),

  getUsageLogs: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.getUsageLogs(placeId),
      method: 'get',
      params: data,
    }),
  deactivate: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.deactivate(placeId),
      method: 'delete',
    }),
  notificationsList: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationsList(placeId),
      method: 'get',
      params: data,
    }),
  ai: {
    token: ({ placeId }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.ai.token({ placeId }),
        method: 'get',
        signal,
      }),
    topics: ({ placeId, query }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.ai.topics(
          { placeId, language: query.language, request_id: query.request_id },
          query,
        ),
        method: 'get',
        signal,
      }),
    content: ({ placeId, data }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.ai.content({ placeId }),
        method: 'post',
        data,
        signal,
      }),
    keywords: ({ placeId, data }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.ai.keywords({ placeId }),
        method: 'post',
        data,
        signal,
      }),
    image: {
      generate: ({ placeId, data }, headers, signal) =>
        axios({
          headers,
          url: endpointConfig.ai.image.generate({ placeId }),
          method: 'post',
          data,
          signal,
        }),
      recent: ({ placeId, data }, headers, signal) =>
        axios({
          headers,
          url: endpointConfig.ai.image.recent({ placeId }),
          method: 'post',
          data,
          signal,
        }),
    },
    languages: ({ placeId }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.ai.languages({ placeId }),
        method: 'get',
        signal,
      }),
    rateMessage: ({ placeId, data }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.ai.rateMessage({ placeId }),
        method: 'post',
        data,
        signal,
      }),
  },
  notificationDetail: ({ placeId, notiId, language }, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationDetail({ placeId, notiId }),
      method: 'get',
      params: { language },
    }),
  getUploadFileUrl: (placeId, headers) =>
    axios({
      headers,
      url: endpointConfig.getUploadFileUrl(placeId),
      method: 'post',
    }),
  importCSV: {
    validate: ({ placeId, data, controller }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importCSV.validate({ placeId }),
        signal: controller.signal,
        method: 'post',
        data,
      })
    },
    singleValidate: (
      { placeId, subscriber, index, processId, errors, signal },
      headers,
    ) => {
      return axios({
        headers,
        url: endpointConfig.importCSV.singleValidate({ placeId }),
        method: 'post',
        data: { subscriber, index, processId, errors },
        signal,
      })
    },
    importSubscribers: ({ placeId, data, controller }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importCSV.importSubscribers({ placeId }),
        signal: controller.signal,
        method: 'post',
        data,
      })
    },
    cancelImport: ({ placeId, importId }, headers) => {
      return fetch(
        endpointConfig.importCSV.cancelImport({ placeId, importId }),
        {
          method: 'DELETE',
          headers,
        },
      )
    },
    checkSubscribersImport: ({ placeId }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importCSV.checkSubscribersImport({ placeId }),
        method: 'get',
      })
    },
    getSubscribersImportDetails: ({ placeId }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importCSV.getSubscribersImportDetails({
          placeId,
        }),
        method: 'get',
      })
    },
    getSubscribersImportErrors: ({ placeId, processId }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importCSV.getSubscribersImportErrors({
          placeId,
          processId,
        }),
        method: 'get',
      })
    },
  },
  importExternalSource: {
    getSISList: ({ placeId }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importExternalSource.getSISList({ placeId }),
        method: 'get',
      })
    },
    importSubscribers: ({ placeId, data }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importExternalSource.importSubscribers({ placeId }),
        method: 'post',
        data,
      })
    },
    getSubscribersImportDetails: ({ placeId }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importExternalSource.getSubscribersImportDetails({
          placeId,
        }),
        method: 'get',
      })
    },
    getSyncSetting: ({ placeId }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importExternalSource.syncSettings({
          placeId,
        }),
        method: 'get',
      })
    },
    updateSyncSetting: ({ placeId, data }, headers) => {
      return axios({
        headers,
        url: endpointConfig.importExternalSource.syncSettings({
          placeId,
        }),
        method: 'patch',
        data,
      })
    },
  },
  getDefaultInitailTemplate: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.defaultInitailTemplate({ placeId }),
      method: 'get',
    }),
  notificationUnsend: (notificationId, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationUnsend(),
      method: 'post',
      data: { json: { id: notificationId } },
    }),
  notificationCancelEvent: ({ notificationId, reason }, headers) =>
    axios({
      headers,
      url: endpointConfig.notificationCancelEvent(notificationId),
      method: 'patch',
      data: { reason },
    }),
  activeTeamMembers: ({ placeId, userIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMembersV2.activeTeamMembers(placeId),
      method: 'patch',
      data: { userIds },
    }),

  resendInvitedTeamMembers: ({ placeId, userIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMembersV2.resendInvitedTeamMembers(placeId),
      method: 'patch',
      data: { userIds },
    }),

  suspendTeamMembers: ({ placeId, userIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.teamMembersV2.suspendTeamMembers(placeId),
      method: 'patch',
      data: { userIds },
    }),

  pollByNotification: (
    { placeId, notificationId, pollId, language },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.poll.pollByNotification({
        placeId,
        notificationId,
        pollId,
      }),
      method: 'get',
      params: { language },
    }),

  pollVoters: ({ pollId, optionId, language }, headers) =>
    axios({
      headers,
      url: endpointConfig.poll.pollVoters({
        pollId,
        optionId,
      }),
      method: 'get',
      params: { language },
    }),

  endPoll: ({ placeId, notificationId }, headers) =>
    axios({
      headers,
      url: endpointConfig.poll.endPoll({
        placeId,
        notificationId,
      }),
      method: 'PATCH',
    }),

  credits: {
    get: ({ placeId }, headers, signal) =>
      axios({
        headers,
        url: endpointConfig.credits.get({ placeId }),
        method: 'get',
        signal,
      }),
  },
  previewAiUsageHistory: ({ placeId, requestId }, headers) =>
    axios({
      headers,
      url: endpointConfig.previewAiUsageHistory({
        placeId,
        requestId,
      }),
      method: 'get',
    }),
  getUploadImageConfig: ({ placeId }, headers) =>
    axios({
      headers,
      url: endpointConfig.getUploadImageConfig({ placeId }),
      method: 'get',
    }),
  uploadJsonToS3: ({ url, jsonObject, progressCallback, controller }) =>
    axios({
      method: 'put',
      url: url,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(jsonObject),
      onUploadProgress: (progressEvent) => {
        progressCallback(progressEvent.progress)
      },
    }),
  uploadImageWithUrlS3: ({ url, file }, headers) =>
    axios({
      method: 'put',
      url: url,
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data',
      },
      data: file,
    }),

  aiUsageLog: {
    getAiUsageLogt: ({ placeId, data }, headers) =>
      axios({
        headers,
        url: endpointConfig.aiUsageLog.getAiUsageLogt(placeId),
        method: 'get',
        params: data,
      }),
    getAiUsageDetail: ({ placeId, requestId }, headers) =>
      axios({
        headers,
        url: endpointConfig.aiUsageLog.getAiUsageDetail({ placeId, requestId }),
        method: 'get',
      }),
  },
  getSubscriberByIdV2: ({ subscriberId, type }, headers) =>
    axios({
      headers,
      url: endpointConfig.getSubscriberByIdV2(subscriberId),
      method: 'get',
    }),
  getGroupForSubscriberAttach: (
    { placeId, type, limit, page, search },
    headers,
  ) =>
    axios({
      headers,
      url: endpointConfig.getGroupForSubscriberAttach(placeId),
      method: 'get',
      params: { type, limit, page, search },
    }),
  getTopicsForSubscriberAttach: ({ placeId, limit, page, search }, headers) =>
    axios({
      headers,
      url: endpointConfig.getTopicsForSubscriberAttach(placeId),
      method: 'get',
      params: { limit, page, search },
    }),
  attachTopicToSubscriber: ({ placeId, subscriberId, topicIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.attachTopicToSubscriber({
        placeId,
        subscriberId,
      }),
      method: 'put',
      data: { topicIds },
    }),
  attachGroupToSubscriber: ({ placeId, subscriberId, tagIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.attachGroupToSubscriber({
        placeId,
        subscriberId,
      }),
      method: 'put',
      data: { tagIds },
    }),
  updateProfileDetailOfSubscriber: ({ placeId, subscriberId, info }, headers) =>
    axios({
      headers,
      url: endpointConfig.updateProfileDetailOfSubscriber({
        placeId,
        subscriberId,
      }),
      method: 'put',
      data: info,
    }),
  deleteSubscribersV2: (placeId, requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribersV2.delete(placeId),
      method: 'delete',
      data: requestData,
    }),
  addOneByOneSubscriber: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribersV2.addOneByOne(requestData.placeId),
      method: 'post',
      data: requestData.data,
    }),
  inviteSubscribers: (requestData, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribersV2.invite(requestData.placeId),
      method: 'post',
      data: requestData.data,
    }),
  exportSubscribers: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.subscribersV2.exportSubscribers(placeId),
      method: 'get',
      params: data,
    }),
  getComments: ({ placeId, notiId }, headers) =>
    axios({
      headers,
      url: endpointConfig.comments.comment({ placeId, notiId }),
      method: 'get',
    }),

  addComment: ({ placeId, notiId, payload }, headers) =>
    axios({
      headers,
      url: endpointConfig.comments.comment({ placeId, notiId }),
      method: 'post',
      data: payload,
    }),

  updateComment: ({ placeId, notiId, payload, commentId }, headers) =>
    axios({
      headers,
      url: endpointConfig.comments.commentById({
        placeId,
        notiId,
        commentId,
      }),
      method: 'patch',
      data: payload,
    }),

  deleteComment: ({ placeId, notiId, commentId }, headers) =>
    axios({
      headers,
      url: endpointConfig.comments.commentById({
        placeId,
        notiId,
        commentId,
      }),
      method: 'delete',
    }),
  getActivity: ({ placeId, subscriberId, page, limit }, headers) =>
    axios({
      headers,
      url: endpointConfig.getActivity({
        placeId,
        subscriberId,
      }),
      method: 'get',
      params: { page, limit },
    }),

  saveMessageAsDraft: ({ placeId, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.saveMessageAsDraft(placeId),
      method: 'post',
      data,
    }),
  editDraftMessage: ({ placeId, id, data }, headers) =>
    axios({
      headers,
      url: endpointConfig.draftMessage({ placeId, id }),
      method: 'patch',
      data,
    }),
  getDraftMessage: ({ placeId, id }, headers) =>
    axios({
      headers,
      url: endpointConfig.draftMessage({ placeId, id }),
      method: 'get',
    }),
  deleteDraftMessage: ({ placeId, id }, headers) =>
    axios({
      headers,
      url: endpointConfig.draftMessage({ placeId, id }),
      method: 'delete',
    }),

  message: {
    metric: {
      count: ({ messageId }, headers) =>
        axios({
          headers,
          url: endpointConfig.message.metric.count({
            messageId,
          }),
          method: 'get',
        }),
      recipientsByMetrics: (
        { messageId, metricsType, pageSize, pageAfter, messageChannels },
        headers,
      ) =>
        axios({
          headers,
          url: endpointConfig.message.metric.recipientsByMetrics({
            messageId,
            metricsType,
            pageSize,
            pageAfter,
            messageChannels,
          }),
          method: 'get',
        }),
    },
  },
  authV2: {
    verifyEmail: (payload) =>
      axios({
        url: endpointConfig.authV2.verifyEmail(),
        method: 'post',
        data: payload,
      }),
    authVerify: (payload) =>
      axios({
        url: endpointConfig.authV2.authVerify(),
        method: 'post',
        data: {
          ...payload,
          clientId: apiConfigs.clientId,
          clientSecret: apiConfigs.clientSecret,
        },
      }),
    otpRequest: (payload) =>
      axios({
        url: endpointConfig.authV2.otpRequest(),
        method: 'post',
        data: {
          ...payload,
          clientId: apiConfigs.clientId,
          clientSecret: apiConfigs.clientSecret,
        },
      }),
    healtCheck: () =>
      axios({
        url: endpointConfig.authV2.healtCheck(),
        method: 'get',
      }),
    forgotPassword: (payload) =>
      axios({
        url: endpointConfig.authV2.forgotPassword(),
        method: 'post',
        data: {
          ...payload,
          clientId: apiConfigs.clientId,
          clientSecret: apiConfigs.clientSecret,
        },
      }),
    resetPassword: (payload) =>
      axios({
        url: endpointConfig.authV2.resetPassword(),
        method: 'post',
        data: {
          ...payload,
          clientId: apiConfigs.clientId,
          clientSecret: apiConfigs.clientSecret,
        },
      }),
    setPassword: ({ password }, headers) =>
      axios({
        headers,
        url: endpointConfig.authV2.setPassword(),
        method: 'post',
        data: {
          password,
        },
      }),
    transferAccount: (payload, headers) =>
      axios({
        headers,
        url: endpointConfig.authV2.transferAccount(),
        method: 'post',
        data: payload,
      }),
    changePassword: (payload, headers) =>
      axios({
        headers,
        url: endpointConfig.authV2.changePassword(),
        method: 'post',
        data: payload,
      }),

    registration: (payload) =>
      axios({
        url: endpointConfig.authV2.registration(),
        method: 'post',
        data: {
          ...payload,
          clientId: apiConfigs.clientId,
          clientSecret: apiConfigs.clientSecret,
        },
      }),
    provider: (payload) =>
      axios({
        url: endpointConfig.authV2.provider(),
        method: 'post',
        data: {
          ...payload,
          clientId: apiConfigs.clientId,
          clientSecret: apiConfigs.clientSecret,
        },
      }),
    voucher: {
      approve: ({ id, parentId }, headers) =>
        axios({
          headers,
          url: endpointConfig.authV2.voucher.approve(),
          method: 'post',
          data: {
            id,
            parentId,
          },
        }),
      reject: ({ id, parentId }, headers) =>
        axios({
          headers,
          url: endpointConfig.authV2.voucher.reject(),
          method: 'post',
          data: {
            id,
            parentId,
          },
        }),
    },
    favotites: {
      add: ({ alertId, userId }, headers) =>
        axios({
          headers,
          url: endpointConfig.favorites.add(alertId),
          method: 'post',
          data: {
            userId,
          },
        }),
      remove: ({ alertId, userId }, headers) =>
        axios({
          headers,
          url: endpointConfig.favorites.remove(alertId),
          method: 'DELETE',
          data: {
            userId,
          },
        }),
    },
  },
  onboarding: {
    googleCurated: (payload, headers) =>
      axios({
        headers,
        url: endpointConfig.onboarding.googleCurated(),
        method: 'post',
        data: payload,
      }),
    checkGoogleCurated: (payload, headers) =>
      axios({
        headers,
        url: endpointConfig.onboarding.checkGoogleCurated(),
        method: 'post',
        data: payload,
      }),
    descriptionAI: ({ placeId, payload }, headers) =>
      axios({
        headers,
        url: endpointConfig.onboarding.descriptionAI(placeId),
        method: 'POST',
        data: payload,
      }),
    setupPlace: ({ payload }, headers) =>
      axios({
        headers,
        url: endpointConfig.onboarding.setupPlace(),
        method: 'POST',
        data: payload,
      }),
  },
  profileInfo: {
    getPlaceInfo: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.placeInfo(placeId),
        method: 'get',
      }),
    updatePlaceInfo: ({ placeId, payload }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.placeInfo(placeId),
        method: 'PATCH',
        data: payload,
      }),
    placePublic: ({ placeId, payload }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.placePublic(placeId),
        method: 'PUT',
        data: payload,
      }),
    getOpeningHours: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.openingHours(placeId),
        method: 'GET',
      }),
    updateOpeningHours: ({ placeId, payload }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.openingHours(placeId),
        method: 'POST',
        data: payload,
      }),
    getGallery: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.gallery(placeId),
        method: 'GET',
      }),
    deletePhoto: ({ placeId, payload, signal }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.gallery(placeId),
        method: 'DELETE',
        data: payload,
        signal,
      }),
    uploadPhoto: (
      { placeId, payload, onProgressBar, index, signal },
      headers,
    ) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.gallery(placeId),
        method: 'POST',
        data: payload,
        signal,
        onUploadProgress: (event) => {
          onProgressBar(event, index)
        },
      }),
    getLandingPage: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.landingPage(placeId),
        method: 'GET',
      }),
    updateLandingPage: ({ placeId, payload }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.landingPage(placeId),
        method: 'PATCH',
        data: payload,
      }),
    confirmNewPlaceEmail: ({ placeId, token }, headers) =>
      axios({
        headers,
        url: endpointConfig.profileInfo.confirmNewPlaceEmail(placeId),
        method: 'PATCH',
        data: { token },
      }),
  },
  integration: {
    getSetting: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.integration.getSetting({ placeId }),
        method: 'get',
      }),
    line: ({ placeId, profileName, profileUrl }, headers) =>
      axios({
        headers,
        url: endpointConfig.integration.line(placeId),
        method: 'post',
        data: { profileName, profileUrl },
      }),
    lineDisconnect: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.integration.lineDisconnect(placeId),
        method: 'delete',
      }),
    getTwitterToken: ({ placeId }, headers) =>
      axios({
        headers,
        url: endpointConfig.integration.getTwitterToken(placeId),
        method: 'get',
      }),
  },
  getTagIds: ({ placeId, groupIds }, headers) =>
    axios({
      headers,
      url: endpointConfig.groupsV2.getTagIds({ placeId }),
      method: 'get',
      params: { groupIds },
    }),
}
export default backend
